import React, { useState, useEffect } from "react";

import { SuccessMessage } from "./AllConfirmationMessages";
import { useCustomDispatch, useCustomLocation } from "../../hooks";
import { updatePatientProfile } from "../../services/apis/setting/updatePatientProfile";
import { useAuthSelector } from "../../services/selectors/authSelector";

import avatar from "../../assets/images/userimg.png";
import contract from "../../assets/images/agreement.svg";
import * as Icons from "../../assets/svg/icons";
import * as routeName from "../../routes/routeName";
import {
	shouldMaskPatientDetails,
	maskPatientNamePart,
	maskPatientPhoneLike,
	maskPatientAddress,
} from "../../utils/helper/patientMasking";

/** Get patient initials: last name + first name (e.g. "GS", "H") */
const getPatientInitials = (lastName, firstName) => {
	const last = (lastName || "").trim().charAt(0).toUpperCase();
	const first = (firstName || "").trim().charAt(0).toUpperCase();
	if (last && first) return `${last}${first}`;
	if (last) return last;
	if (first) return first;
	return "?";
};

/** True when image is empty or the default static user image from API/assets */
const isDefaultUserImage = (src) => {
	if (!src || typeof src !== "string") return true;
	return /user\.png|userimg/i.test(src) || src.includes("userimg");
};

/** Badge color palette (bg, text) – same patient gets same color */
const INITIALS_BADGE_COLORS = [
	{ bg: "#E0F2F7", text: "#1A237E" },
	{ bg: "#FCE4EC", text: "#880E4F" },
	{ bg: "#E8F5E9", text: "#1B5E20" },
	{ bg: "#FFF8E1", text: "#E65100" },
	{ bg: "#E3F2FD", text: "#0D47A1" },
];

const getInitialsBadgeStyle = (lastName, firstName) => {
	const key = `${(lastName || "")}-${(firstName || "")}`;
	let index = 0;
	for (let i = 0; i < key.length; i++) index += key.charCodeAt(i);
	const { bg, text } = INITIALS_BADGE_COLORS[Math.abs(index) % INITIALS_BADGE_COLORS.length];
	return { backgroundColor: bg, color: text };
};

export const displaySelectedImage = (
	event,
	setSelectedImage,
	index,
	setShowToast,
	data,
	dispatch
) => {
	if (event.target.files.length > 0) {
		const file = event.target.files[0];

		const requestedData = {
			pharmacy_id: data.pharmacy_id,
			patient_id: data.id,
			profile_image: file,
		};
		dispatch(updatePatientProfile(requestedData));

		if (file.type.startsWith("image/")) {
			const reader = new FileReader();

			reader.onload = function (e) {
				setSelectedImage(e.target.result, index);
				setShowToast((prevState) => ({ ...prevState, successToast: true }));

				setTimeout(() => {
					setShowToast((prevState) => ({ ...prevState, successToast: false }));
				}, 2000);
			};

			reader.readAsDataURL(file);
		}
	}
};

export const PatientDetails = ({
	data,
	totalRxData,
	index,
	patientImg,
	openIndex,
	onDropdownToggle,
	children,
	headerRight,
	headerAction,
	profileImageInputId,
	alwaysShowChildren,
}) => {
	const profileInputId = profileImageInputId ?? `selectProfileImage-${index}`;
	const dispatch = useCustomDispatch();
	const location = useCustomLocation();
	const { pharmacyId } = useAuthSelector();

	const [selectedImage, setSelectedImage] = useState(patientImg || avatar);
	const [showToast, setShowToast] = useState({ successToast: "" });

	useEffect(() => {
		if (data?.profile_image !== selectedImage) {
			setSelectedImage(avatar);
		}
	}, [selectedImage, data]);

	const showInitialsBadge = isDefaultUserImage(selectedImage);
	const initials = getPatientInitials(data?.last_name, data?.first_name);
	const initialsStyle = getInitialsBadgeStyle(data?.last_name, data?.first_name);
	const shouldMaskDetails = shouldMaskPatientDetails(pharmacyId);
	const displayLastName = shouldMaskDetails
		? maskPatientNamePart(data?.last_name, { wrap: true })
		: data?.last_name;
	const displayFirstName = shouldMaskDetails
		? maskPatientNamePart(data?.first_name)
		: data?.first_name;
	const displayPhn = shouldMaskDetails
		? maskPatientPhoneLike(data?.phn)
		: data?.phn;
	const displayPhone = shouldMaskDetails
		? maskPatientPhoneLike(data?.phone)
		: data?.phone;
	const displayFullAddress = shouldMaskDetails
		? maskPatientAddress(data?.full_address)
		: data?.full_address;

	return (
		<React.Fragment>
			{/* Header: avatar, name, badge, delivery time / external link */}
			<div className="flex items-start justify-between">
				<div className="flex items-center gap-1.5">
					{showInitialsBadge ? (
						<div
							role="button"
							tabIndex={0}
							className="rounded-full w-9 h-9 flex-shrink-0 cursor-pointer flex items-center justify-center text-xs font-semibold uppercase"
							style={initialsStyle}
							onClick={() => {
								const filePicker = document.getElementById(profileInputId);
								filePicker?.click();
							}}
							onKeyDown={(e) => {
								if (e.key === "Enter" || e.key === " ") {
									e.preventDefault();
									document.getElementById(profileInputId)?.click();
								}
							}}
							aria-label={`Profile: ${data?.last_name || ""} ${data?.first_name || ""}`}
						>
							{initials}
						</div>
					) : (
						<img
							src={selectedImage}
							alt="avatar"
							className="rounded-full w-9 h-9 object-cover bg-gray-200 flex-shrink-0 cursor-pointer"
							onClick={() => {
								const filePicker = document.getElementById(profileInputId);
								filePicker?.click();
							}}
						/>
					)}
					<input
						name="avatar"
						accept="image/jpeg, image/jpg, image/png,capture=camera"
						onChange={(event) =>
							displaySelectedImage(
								event,
								setSelectedImage,
								index,
								setShowToast,
								data,
								dispatch
							)
						}
						type="file"
						id={profileInputId}
						className="hidden"
					/>
					<div>
						<h2 className="text-base font-semibold uppercase tracking-tight flex items-center gap-1">
							{displayLastName} {displayFirstName}
							{totalRxData && (
								<span className="flex items-center justify-center h-5 w-5 text-xs rounded-full bg-green-600 text-white" >
									{totalRxData}
								</span>
							)}
						</h2>
						<p className="text-xs text-gray-400 -mt-1 uppercase">
							{data?.gender && <span>{data.gender}</span>}
							{data?.gender && data?.dob && " · "}
							{data?.dob && <span>{data.dob}</span>}
						</p>
					</div>
				</div>
				{headerRight != null || headerAction != null ? (
					<div className="flex items-center gap-2">
						{headerRight}
						{headerAction}
					</div>
				) : location.pathname === routeName.DRIVER_ROUTE_DELIVERED ? (
					<div style={{ fontSize: "10px" }} className="text-gray-400 flex items-center">
						{data?.delivery_human_time}
					</div>
				) : null}
			</div>

			{/* Contact: PHN, Phone – only show card if value exists */}
			{(data?.phn || data?.phone) && (
				<div className="grid grid-cols-2 gap-1.5 mt-1.5">
					{data?.phn && (
						<div className="rounded-lg border border-gray-100 bg-gray-50 py-1 px-2">
							<div className="flex items-center gap-2">
								<span className="inline-flex w-4 h-4 flex-shrink-0 items-center justify-center overflow-hidden text-green-600 [&_svg]:!h-full [&_svg]:!w-full">
									<svg fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
										<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H5a2 2 0 00-2 2v9a2 2 0 002 2h14a2 2 0 002-2v-8a2 2 0 00-2-2h-5m-4 0V5a2 2 0 114 0v1m-4 0a2 2 0 104 0m-5 8a2 2 0 100-4 2 2 0 000 4zm0 0c1.306 0 2.417.835 2.83 2M9 14a3.001 3.001 0 00-3 3c0 1.23 1.24 2.37 2.83 2.83M15 11h3m-3 4h2" />
									</svg>
								</span>
								<div className="leading-tight">
									<p className="text-xs text-gray-400 uppercase tracking-wide" style={{ fontSize: "10px" }}>
										PHN
									</p>
									<p className="text-xs text-gray-500 break-all">{displayPhn}</p>
								</div>
							</div>
						</div>
					)}

					<div className="flex items-center gap-1.5">
						{data?.phone && (
							<a href={`tel:${data.phone}`} className="w-full">
								<div className="rounded-lg border border-gray-100 bg-gray-50 py-1 px-2 w-full">
									<div className="flex items-center gap-2">
										<span className="inline-flex w-4 h-4 flex-shrink-0 items-center justify-center text-green-600 [&_svg]:!h-full [&_svg]:!w-full">
											<Icons.PhoneIcon color="#1BAC25" />
										</span>
										<div className="leading-tight">
											<p className="text-xs text-gray-400 uppercase tracking-wide" style={{ fontSize: "10px" }}>
												PHONE
											</p>
											<p className="text-xs text-gray-500 break-all">{displayPhone}</p>
										</div>
									</div>
								</div>
							</a>
						)}
						{/* VideoCallButton slot – same structure as Driver; no video call in Instore */}
					</div>
				</div>
			)}

			<div className="flex items-center justify-between gap-2 mt-1">
				{data?.full_address && (
					<div className="rounded-lg border border-gray-100 bg-gray-50 py-1 px-2 ">
						<a target="_blank" href={data?.map_url} rel="noreferrer" key="mapLink">
							<div className="flex items-center gap-2">
								<span className="inline-flex w-4 h-4 flex-shrink-0 items-center justify-center [&_svg]:!h-full [&_svg]:!w-full">
									<Icons.LocationIcon color="#1BAC25" />
								</span>
								<div className="leading-tight">
									<p className="text-xs text-gray-500 break-words">{displayFullAddress}</p>
								</div>
							</div>
						</a>
					</div>
				)}

				{onDropdownToggle != null && (
					<button
						className="p-2.5 rounded-md bg-gray-100"
						onClick={() => onDropdownToggle(index, data)}
						type="button"
					>
						{openIndex === index ? (
							<Icons.DropdownBoxIcon2 />
						) : (
							<Icons.DropdownBoxIcon1 />
						)}
					</button>
				)}
			</div>
			

				
						{data?.delivey_note && (
							<p
								className="mt-1.5 bg-fuchsia-200 p-1 pl-1.5 rounded-md"
								style={{
									width: "fit-content",
									color: "black",
									fontSize: "13px",
									fontWeight: "bold",
									backgroundColor: "rgb(132 204 22)",
								}}
							>
								<span className="text-red-700">Note: </span>
								{data?.delivey_note}
							</p>
						)}
					
					

						
					


		


			{(alwaysShowChildren || openIndex === index) && children}

			{showToast.successToast && (
				<label className="fixed -left-3 -right-0 bottom-0 z-50">
					<SuccessMessage
						message={"Profile Updated Successfully."}
						setShowToast={() =>
							setShowToast((prevState) => ({
								...prevState,
								successToast: false,
							}))
						}
					/>
				</label>
			)}
		</React.Fragment>
	);
};

export const PatientMobileAndLocation = ({ data, setIsTransferRequest, isTransferRequest, index }) => {
	const [showModal, setShowModal] = useState(false);
	const [patient_id, setPatient_id] = useState(null);

	const { driverSelectedPharmacy } = useAuthSelector();
	const location = useCustomLocation();

	const handleToggleTransferConsent = () => {
		setShowModal(true);
		setPatient_id(data?.patient_id);
		if (patient_id !== data?.patient_id) {
			setIsTransferRequest(null);
		}
	};

	return (
		<React.Fragment>
			<div className="flex items-center gap-3">
				{driverSelectedPharmacy?.pharmacies?.transfer_concent === 1
					? location.pathname === routeName.DRIVER_ROUTE_DELIVERED ||
						location.pathname === routeName.pending_pharmacist_signatures ||
						location.pathname === routeName.Counselling
						? null
						: data?.pharmacy_transfer_concents === null
							? null
							: (
								<button className="px-1 py-1.5 rounded-md  bg-opacity-10">
									<a>
										<img src={contract} alt="mapPic" className="w-6 h-6" />
									</a>
								</button>
							)
					: null}
			</div>
		</React.Fragment>
	);
};

export const TransferConsentMessage = ({ data }) => {
	const location = useCustomLocation();

	return <React.Fragment></React.Fragment>;
};
